import React, { useState, useMemo, memo } from "react";
import { Grid, Box, Tooltip, Button, Checkbox } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { round, isNull } from "lodash";
import dayjs from "dayjs";
import { alpha } from "@mui/material/styles";
import {
  NOTE_TYPE_VALUE,
  getFormattedNumber,
  prettyNumericRound,
  singleValue,
} from "../../../utils";
import { validPog } from "../../../utils/pogValidator";
import { isItemDepartment } from "../../../utils/deptValidator";
import useStableCallback from "../../../hooks/useStableCallback";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomInput from "../../../components/CustomInput";
import NumericLabel from "../../../components/NumericLabel";
import AutoCompleteInput from "../AutoCompleteInput";
import AddNoteOption from "../AddNoteOption";
import {
  NOTE_SAVE_TYPE,
  setNoteSaveType,
  resetNoteSaveType,
} from "../../../store/notesSlice";
import { useAddNoteMutation, notesApi } from "../../../services/notesApi";

const PogExpandPanelSx = {
  fontSize: "0.688rem",
  paddingTop: "0px !important",
  paddingBottom: "5px",
  px: 2,

  "& .bold-label": { fontWeight: 500, mr: 0.5 },
  "& .truncated-value": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

const ColumnSx = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  mr: 0.5,
};

const ColumnBoxSx = {
  height: "1.5rem",
  display: "flex",
  alignItems: "center",
};

const CustomFieldSx = {
  width: "1rem",
  height: "1rem",
  ml: 0.5,
  "&.Mui-disabled": {
    color: (theme) => alpha(theme.palette.success.main, 0.5),
  },
};

const SaveBtnSX = {
  lineHeight: "unset",
  fontSize: "0.65rem",
  mt: 0.5,
};

const ExpandedPogView = ({
  data,
  handleProcessData,
  releaseStatus,
  handlePogLevelSave,
  isDuplicateSelection = false,
  freezePog = false,
}) => {
  const dispatch = useDispatch();
  const [addNote] = useAddNoteMutation();
  const [notesLoader, setNotesLoader] = useState(false);
  const [selectedNote, setSelectedNote] = useState("");
  const storeId = useSelector((store) => store.global.store.id);
  const noteSaveType = useSelector((store) => store.notes.saveType);
  const user = useSelector((store) => store.global.user);

  const isPartOfItemsDept = isItemDepartment(data);

  const { data: plannerNotesData } =
    notesApi.endpoints.getNotesForPlanogram.useQuery({
      noteType: NOTE_TYPE_VALUE.planner,
      storeId,
    });
  const { data: retailerNotesData } =
    notesApi.endpoints.getNotesForPlanogram.useQuery({
      noteType: NOTE_TYPE_VALUE.retailer,
      storeId,
    });

  const plannerNoteOptions = useMemo(() => {
    return plannerNotesData?.map((note) => ({
      ...note,
      title: note.Notes,
      id: note.NotesId,
    }));
  }, [plannerNotesData]);

  const retailerNoteOptions = useMemo(() => {
    const list = retailerNotesData?.map((note) => ({
      ...note,
      title: note.Notes,
      id: note.NotesId,
    }));
    return list;
  }, [retailerNotesData]);

  const handleDiscountPercentage = useStableCallback((field) => (event) => {
    let newValue = String(event?.target?.value).trim();
    let currentValue = null;
    if (newValue.split(".").length > 1) {
      const n = Number(newValue);
      if (n > 100 || !isNaN(n)) {
        currentValue = newValue;
      } else {
        return event.preventDefault();
      }
    } else {
      currentValue = !isNull(newValue) ? Number(newValue) : null;
      if (currentValue > 100 || isNaN(currentValue)) {
        return event.preventDefault();
      }
    }
    handleProcessData({ field, value: currentValue });
  });

  const handleDiscountBlur = useStableCallback((field) => (event) => {
    const newValue = event.target.value;
    if (String(newValue)?.indexOf(".") !== -1) {
      handleProcessData({ field, value: prettyNumericRound(newValue, 2) });
    }
  });

  const handleDatingDays = useStableCallback((event) => {
    let newValue = event.target.value;
    const currentValue = !isNull(newValue) ? Number(newValue) : null;
    newValue = currentValue;
    if (currentValue > 365 || isNaN(currentValue)) {
      return event.preventDefault();
    }
    handleProcessData({ field: "DatingDays", value: newValue });
  });

  const handleChange = useStableCallback((field) => (event) => {
    handleProcessData({ field, value: event?.target?.value });
  });

  const handleCustomization = useStableCallback((field) => (event) => {
    handleProcessData({ field, value: event?.target?.checked });
  });

  const handleNotesChange = useStableCallback(
    (field) =>
      (event, selectedValue = {}) => {
        if (selectedValue?.id) {
          handleProcessData({ field, value: selectedValue?.id });
        } else if (selectedValue?.title) {
          const selectedOption = event.target.value || selectedValue.title;
          handleProcessData({ field, value: selectedOption });
        } else {
          handleProcessData({ field, value: "" });
        }
      }
  );

  const handleNoteDialogClose = useStableCallback(() => {
    dispatch(resetNoteSaveType());
  });

  const handleNoteTypeSaveChange = useStableCallback((event) => {
    dispatch(setNoteSaveType());
    event.stopPropagation();
  });

  const addNewNoteHandler = (field) => async (newValue) => {
    setSelectedNote(field);
    setNotesLoader(true);
    await addNote({
      notesType:
        field === NOTE_TYPE_VALUE.retailer
          ? NOTE_TYPE_VALUE.retailer
          : NOTE_TYPE_VALUE.planner,
      notes: newValue?.title?.trim(),
      createdBy: user.email,
      storeId: noteSaveType === NOTE_SAVE_TYPE[0] ? storeId : null,
    })
      .then(({ data }) => {
        const currentSelection = { ...newValue, id: data.NotesId };
        if (field === "RetailerNotes") {
          retailerNoteOptions.push(currentSelection);
        } else {
          plannerNoteOptions.push(currentSelection);
        }
        handleProcessData({ field, value: currentSelection.id });
      })
      .finally(() => {
        setNotesLoader(false);
        setSelectedNote("");
      });
  };

  const addNoteOptionRenderer = useStableCallback((params) => (
    <AddNoteOption inputValue={params} />
  ));

  const memoizedPlannerValue = useMemo(() => {
    return (
      plannerNoteOptions?.find((pn) => pn.id === Number(data?.PlannerNotes)) ||
      ""
    );
  }, [data?.PlannerNotes, plannerNoteOptions]);

  const memoizedRetailerValue = useMemo(() => {
    return (
      retailerNoteOptions?.find(
        (pn) => pn.id === Number(data?.RetailerNotes)
      ) || ""
    );
  }, [data?.RetailerNotes, retailerNoteOptions]);

  const disabledAllFields =
    !validPog(data) || freezePog || isDuplicateSelection;

  const selectedNo = data?.Selected !== singleValue.yes;

  return (
    <Grid container sx={PogExpandPanelSx} justifyContent="space-between">
      <Grid item xs="auto" md={1.2} sx={ColumnSx}>
        <Box sx={ColumnBoxSx}>
          <Tooltip title={data?.Category ?? ""} arrow>
            <div className="truncated-value">
              <span className="bold-label">Category:</span>
              {data?.Category}
            </div>
          </Tooltip>
        </Box>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Core:</span>
          {isPartOfItemsDept ? (
            <>No</>
          ) : (
            <>{data?.Core === singleValue.yes ? "Yes" : "No"}</>
          )}
        </Box>
      </Grid>
      <Grid item xs="auto" md={1.45} sx={ColumnSx}>
        <Box sx={ColumnBoxSx}>
          <Tooltip title={data?.PONumber ?? ""} arrow>
            <div className="truncated-value">
              <span className="bold-label">PO Number:</span>
              {data?.AllItemsFromFillIn ? (
                <span> -</span>
              ) : !!data?.PONumber?.trim() ? (
                data?.PONumber
              ) : (
                "-"
              )}
            </div>
          </Tooltip>
        </Box>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Release Date:</span>
          {data?.AllItemsFromFillIn ? (
            <span className="fill-space-dash"> -</span>
          ) : !!data?.ReleaseDate ? (
            dayjs(data?.ReleaseDate).format("MM/DD/YYYY")
          ) : (
            "-"
          )}
        </Box>
      </Grid>
      <Grid item xs="auto" md={1.25} sx={ColumnSx}>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Status:</span>
          {data?.AllItemsFromFillIn ? <span> -</span> : releaseStatus}
        </Box>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">POG Qty:</span>
          <NumericLabel isSeparator value={round(data?.Qty || 0, 2)} />
        </Box>
      </Grid>
      <Grid item xs="auto" md={1.2} sx={ColumnSx}>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Adj. Qty:</span>
          {data?.AllItemsFromFillIn ? (
            <span> -</span>
          ) : (
            <NumericLabel isSeparator value={round(data?.AdjQty || 0, 2)} />
          )}
        </Box>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Ext. Cost:</span>
          {data?.AllItemsFromFillIn ? (
            <span> -</span>
          ) : (
            `$${getFormattedNumber(prettyNumericRound(data?.ExtCost || 0))}`
          )}
        </Box>
      </Grid>
      <Grid item xs="auto" sx={{ ...ColumnSx, alignItems: "flex-end" }}>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Discount Multiplier:</span>
          {data?.AllItemsFromFillIn ? (
            <span> -</span>
          ) : (
            <CustomDropdown
              value={data?.DiscMultiplier || 1}
              inputsx={{ fontSize: "10px" }}
              disabled={
                disabledAllFields ||
                selectedNo ||
                data?.CustomDiscMultiplier ||
                isPartOfItemsDept
              }
              onChange={handleChange("DiscMultiplier")}
            >
              <option value="1">1x</option>
              <option value="2">2x</option>
              <option value="3">3x</option>
              <option value="4">4x</option>
            </CustomDropdown>
          )}
          {data?.CustomDiscMultiplier && (
            <Checkbox
              size="small"
              color="success"
              disableRipple
              sx={CustomFieldSx}
              checked={data?.CustomDiscMultiplier}
              onClick={handleCustomization("CustomDiscMultiplier")}
              disabled={!validPog(data)}
            />
          )}
        </Box>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Qty. Multiplier:</span>
          {data?.AllItemsFromFillIn ? (
            <span> -</span>
          ) : (
            <CustomDropdown
              value={data?.QtyMultiplier || 1}
              inputsx={{ fontSize: "10px" }}
              disabled={
                disabledAllFields ||
                selectedNo ||
                data?.CustomQtyMultiplier ||
                isPartOfItemsDept
              }
              onChange={handleChange("QtyMultiplier")}
            >
              <option value="1">1x</option>
              <option value="2">2x</option>
              <option value="3">3x</option>
              <option value="4">4x</option>
            </CustomDropdown>
          )}
          {data?.CustomQtyMultiplier && (
            <Checkbox
              size="small"
              color="success"
              disableRipple
              sx={CustomFieldSx}
              checked={data?.CustomQtyMultiplier}
              onClick={handleCustomization("CustomQtyMultiplier")}
              disabled={!validPog(data)}
            />
          )}
        </Box>
      </Grid>
      <Grid item xs="auto" sx={{ ...ColumnSx, alignItems: "flex-end" }}>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Core %:</span>
          {data?.AllItemsFromFillIn ? (
            <span> -</span>
          ) : (
            <CustomInput
              sx={{ width: "35px" }}
              value={data?.CoreDiscount || 0}
              disabled={
                disabledAllFields ||
                selectedNo ||
                data?.CustomCoreDiscount ||
                isPartOfItemsDept
              }
              onChange={handleDiscountPercentage("CoreDiscount")}
              onBlur={handleDiscountBlur("CoreDiscount")}
            />
          )}
          {data?.CustomCoreDiscount && (
            <Checkbox
              size="small"
              color="success"
              disableRipple
              sx={CustomFieldSx}
              checked={data?.CustomCoreDiscount}
              onClick={handleCustomization("CustomCoreDiscount")}
              disabled={!validPog(data)}
            />
          )}
        </Box>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Non-Core %:</span>
          {data?.AllItemsFromFillIn ? (
            <span> -</span>
          ) : (
            <CustomInput
              sx={{ width: "35px" }}
              value={data?.NonCoreDiscount || 0}
              disabled={
                disabledAllFields || selectedNo || data?.CustomNonCoreDiscount
              }
              onChange={handleDiscountPercentage("NonCoreDiscount")}
              onBlur={handleDiscountBlur("NonCoreDiscount")}
            />
          )}
          {data?.CustomNonCoreDiscount && (
            <Checkbox
              size="small"
              color="success"
              disableRipple
              sx={CustomFieldSx}
              checked={data?.CustomNonCoreDiscount}
              onClick={handleCustomization("CustomNonCoreDiscount")}
              disabled={!validPog(data)}
            />
          )}
        </Box>
      </Grid>
      <Grid item xs="auto" sx={ColumnSx}>
        <Box sx={{ ...ColumnBoxSx }}>
          <span className="bold-label">Planner Notes:</span>
          {data?.AllItemsFromFillIn ? (
            <span> -</span>
          ) : (
            <Tooltip
              title={memoizedPlannerValue?.title ?? ""}
              disableInteractive
              placement="top"
              arrow
            >
              <div>
                <AutoCompleteInput
                  showAddOption={true}
                  pogNumber={data?.PlanogramNumber}
                  addOptionRenderer={addNoteOptionRenderer}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                  sx={{ margin: 0, width: "9rem" }}
                  loading={
                    selectedNote === NOTE_TYPE_VALUE.planner && notesLoader
                  }
                  onSelect={handleNotesChange(NOTE_TYPE_VALUE.planner)}
                  value={memoizedPlannerValue}
                  optionList={plannerNoteOptions}
                  updateList={addNewNoteHandler(NOTE_TYPE_VALUE.planner)}
                  noteSaveType={noteSaveType}
                  onNoteTypeSaveChange={handleNoteTypeSaveChange}
                  onDialogClose={handleNoteDialogClose}
                  disabled={disabledAllFields}
                />
              </div>
            </Tooltip>
          )}
        </Box>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Retailer Notes:</span>
          {data?.AllItemsFromFillIn ? (
            <span> -</span>
          ) : (
            <Tooltip
              title={memoizedRetailerValue?.title ?? ""}
              disableInteractive
              placement="top"
              arrow
            >
              <div>
                <AutoCompleteInput
                  showAddOption={true}
                  pogNumber={data?.PlanogramNumber}
                  addOptionRenderer={addNoteOptionRenderer}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                  sx={{ margin: 0, width: "9rem" }}
                  loading={
                    selectedNote === NOTE_TYPE_VALUE.retailer && notesLoader
                  }
                  onSelect={handleNotesChange(NOTE_TYPE_VALUE.retailer)}
                  value={memoizedRetailerValue}
                  optionList={retailerNoteOptions}
                  updateList={addNewNoteHandler(NOTE_TYPE_VALUE.retailer)}
                  noteSaveType={noteSaveType}
                  onNoteTypeSaveChange={handleNoteTypeSaveChange}
                  onDialogClose={handleNoteDialogClose}
                  disabled={disabledAllFields}
                />
              </div>
            </Tooltip>
          )}
        </Box>
      </Grid>
      <Grid item xs="auto" sx={{ ...ColumnSx, alignItems: "flex-end", mr: 0 }}>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Dating Days:</span>
          {data?.AllItemsFromFillIn ? (
            <span> -</span>
          ) : (
            <CustomInput
              sx={{ width: "35px" }}
              value={data?.DatingDays || 0}
              disabled={
                disabledAllFields || selectedNo || data?.CustomDatingDays
              }
              onChange={handleDatingDays}
            />
          )}
          {data?.CustomDatingDays && (
            <Checkbox
              size="small"
              color="success"
              disableRipple
              sx={CustomFieldSx}
              checked={data?.CustomDatingDays}
              onClick={handleCustomization("CustomDatingDays")}
              disabled={!validPog(data)}
            />
          )}
        </Box>
        <Button
          variant="contained"
          size="small"
          sx={SaveBtnSX}
          onClick={handlePogLevelSave}
          disabled={disabledAllFields}
        >
          Save
        </Button>
      </Grid>
    </Grid>
  );
};

export default memo(ExpandedPogView);
